import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { db } from "../firebase";

import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import Chart from "react-apexcharts";

import {
  FaArrowLeft,
  FaChartLine,
  FaFutbol,
  FaUsers,
  FaTrophy,
  FaMoneyBillWave,
  FaSyncAlt,
  FaCalendarAlt,
} from "react-icons/fa";

import "./Report.css";

/* =========================================================
   SETTINGS
========================================================= */

const BET_COLLECTION = "antscore";

const CACHE_TIME = 2 * 60 * 1000;

const PERIODS = {
  3: "3 Days",
  7: "7 Days",
  14: "14 Days",
  21: "21 Days",
  28: "28 Days",
};

/* =========================================================
   DATE HELPERS
========================================================= */

/**
 * Converts any supported Firestore/date value into
 * a valid JavaScript Date object.
 */
const getDateValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  try {
    /* Already a JavaScript Date */
    if (value instanceof Date) {
      return Number.isNaN(value.getTime())
        ? null
        : value;
    }

    /* Firestore Timestamp */
    if (
      typeof value?.toDate === "function"
    ) {
      const date = value.toDate();

      return date instanceof Date &&
        !Number.isNaN(date.getTime())
        ? date
        : null;
    }

    /* Firestore serialized Timestamp */
    if (
      typeof value === "object" &&
      typeof value.seconds === "number"
    ) {
      const milliseconds =
        value.seconds * 1000 +
        Math.floor(
          Number(value.nanoseconds || 0) / 1000000
        );

      const date = new Date(milliseconds);

      return Number.isNaN(date.getTime())
        ? null
        : date;
    }

    /* Firebase Timestamp-like object */
    if (
      typeof value === "object" &&
      typeof value._seconds === "number"
    ) {
      const date = new Date(
        value._seconds * 1000
      );

      return Number.isNaN(date.getTime())
        ? null
        : date;
    }

    /* Unix milliseconds */
    if (
      typeof value === "number"
    ) {
      const date = new Date(value);

      return Number.isNaN(date.getTime())
        ? null
        : date;
    }

    /* String date */
    if (
      typeof value === "string"
    ) {
      const trimmed = value.trim();

      if (!trimmed) {
        return null;
      }

      const date = new Date(trimmed);

      return Number.isNaN(date.getTime())
        ? null
        : date;
    }

    return null;
  } catch (error) {
    console.error(
      "Invalid date value:",
      value,
      error
    );

    return null;
  }
};

/**
 * Makes sure we ALWAYS have a Date before
 * calling getFullYear/getMonth/getDate/getTime.
 */
const ensureDate = (value) => {
  const date = getDateValue(value);

  return date instanceof Date &&
    !Number.isNaN(date.getTime())
    ? date
    : null;
};

/* =========================================================
   DATE KEY
========================================================= */

const getDateKey = (value) => {
  const date = ensureDate(value);

  if (!date) {
    return null;
  }

  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1
    ).padStart(2, "0");

  const day =
    String(
      date.getDate()
    ).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/* =========================================================
   FORMAT NUMBER
========================================================= */

const formatNumber = (value) => {
  const number =
    Number(value || 0);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return number.toLocaleString();
};

/* =========================================================
   FORMAT DATE
========================================================= */

const formatDate = (value) => {
  const date =
    ensureDate(value);

  if (!date) {
    return "-";
  }

  return date.toLocaleDateString(
    [],
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  );
};

/* =========================================================
   FORMAT SHORT DATE
========================================================= */

const formatShortDate = (value) => {
  const date =
    ensureDate(value);

  if (!date) {
    return "";
  }

  return date.toLocaleDateString(
    [],
    {
      day: "2-digit",
      month: "short",
    }
  );
};

/* =========================================================
   CACHE
========================================================= */

const getCacheKey = (
  username
) =>
  `reportCache_${username}`;

/* =========================================================
   SAFE NUMBER
========================================================= */

const safeNumber = (value) => {
  const number =
    Number(value);

  return Number.isFinite(number)
    ? number
    : 0;
};

/* =========================================================
   GET BET STAKE
========================================================= */

const getBetStake = (bet) => {
  return safeNumber(
    bet?.stake ??
    bet?.amount ??
    bet?.quantity ??
    0
  );
};

/* =========================================================
   GET BET PROFIT
========================================================= */

const getBetProfit = (bet) => {
  return safeNumber(
    bet?.profit ??
    0
  );
};

/* =========================================================
   GET BET WIN
========================================================= */

const getBetWin = (bet) => {
  return safeNumber(
    bet?.totalWin ??
    0
  );
};

/* =========================================================
   RESULT HELPERS
========================================================= */

const isWin = (bet) => {
  const result =
    String(
      bet?.result || ""
    )
      .trim()
      .toLowerCase();

  const status =
    String(
      bet?.status || ""
    )
      .trim()
      .toLowerCase();

  return (
    result === "win" ||
    result === "won" ||
    status === "win" ||
    status === "won"
  );
};

const isLoss = (bet) => {
  const result =
    String(
      bet?.result || ""
    )
      .trim()
      .toLowerCase();

  const status =
    String(
      bet?.status || ""
    )
      .trim()
      .toLowerCase();

  return (
    result === "lose" ||
    result === "loss" ||
    result === "lost" ||
    status === "lose" ||
    status === "loss" ||
    status === "lost"
  );
};

/* =========================================================
   REPORT COMPONENT
========================================================= */

const Report = () => {
  const navigate =
    useNavigate();

  /* =======================================================
     STATE
  ======================================================= */

  const [
    bets,
    setBets,
  ] = useState([]);

  const [
    period,
    setPeriod,
  ] = useState(7);

  const [
    chartMode,
    setChartMode,
  ] = useState("daily");

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const loggedInUsername =
    localStorage.getItem(
      "username"
    );

  /* =======================================================
     LOAD CACHE
  ======================================================= */

  const loadCache =
    useCallback(
      () => {
        if (
          !loggedInUsername
        ) {
          return null;
        }

        try {
          const raw =
            localStorage.getItem(
              getCacheKey(
                loggedInUsername
              )
            );

          if (!raw) {
            return null;
          }

          const cached =
            JSON.parse(raw);

          if (
            !cached ||
            !Array.isArray(
              cached.data
            )
          ) {
            return null;
          }

          return cached.data;
        } catch (cacheError) {
          console.error(
            "Failed to load report cache:",
            cacheError
          );

          return null;
        }
      },
      [
        loggedInUsername,
      ]
    );

  /* =======================================================
     SAVE CACHE
  ======================================================= */

  const saveCache =
    useCallback(
      (data) => {
        if (
          !loggedInUsername
        ) {
          return;
        }

        try {
          /*
           * Dates cannot be kept as Date objects
           * inside localStorage.
           *
           * Convert them to ISO strings.
           */
          const serializableData =
            Array.isArray(data)
              ? data.map(
                  (bet) => ({
                    ...bet,

                    createdAt:
                      ensureDate(
                        bet.createdAt
                      )?.toISOString() ||
                      null,
                  })
                )
              : [];

          localStorage.setItem(
            getCacheKey(
              loggedInUsername
            ),
            JSON.stringify({
              data:
                serializableData,

              updatedAt:
                Date.now(),
            })
          );
        } catch (cacheError) {
          console.error(
            "Report cache error:",
            cacheError
          );
        }
      },
      [
        loggedInUsername,
      ]
    );

  /* =======================================================
     FETCH BETS
  ======================================================= */

  const fetchBets =
    useCallback(
      async () => {
        if (
          !loggedInUsername
        ) {
          return [];
        }

        const q =
          query(
            collection(
              db,
              BET_COLLECTION
            ),
            where(
              "username",
              "==",
              loggedInUsername
            )
          );

        const snap =
          await getDocs(q);

        const result = [];

        snap.forEach(
          (doc) => {
            const data =
              doc.data();

            /*
             * Try createdAt first.
             * Also support common alternative
             * timestamp fields.
             */
            const createdAt =
              ensureDate(
                data.createdAt ??
                data.timestamp ??
                data.date ??
                data.created_at
              );

            if (!createdAt) {
              return;
            }

            result.push({
              id:
                doc.id,

              betId:
                data.betId ||
                doc.id,

              createdAt,

              amount:
                safeNumber(
                  data.amount ??
                  data.stake ??
                  data.quantity ??
                  0
                ),

              stake:
                safeNumber(
                  data.stake ??
                  data.amount ??
                  data.quantity ??
                  0
                ),

              totalWin:
                safeNumber(
                  data.totalWin ??
                  0
                ),

              profit:
                safeNumber(
                  data.profit ??
                  0
                ),

              result:
                String(
                  data.result ||
                  ""
                )
                  .trim()
                  .toLowerCase(),

              status:
                String(
                  data.status ||
                  ""
                )
                  .trim()
                  .toLowerCase(),

              odds:
                safeNumber(
                  data.odds ??
                  0
                ),

              currency:
                data.currency ||
                "TZS",
            });
          }
        );

        result.sort(
          (a, b) => {
            const dateA =
              ensureDate(
                a.createdAt
              );

            const dateB =
              ensureDate(
                b.createdAt
              );

            return (
              (dateB?.getTime() || 0) -
              (dateA?.getTime() || 0)
            );
          }
        );

        return result;
      },
      [
        loggedInUsername,
      ]
    );

  /* =======================================================
     LOAD REPORT
  ======================================================= */

  const loadReport =
    useCallback(
      async (
        showRefresh = false
      ) => {
        if (
          !loggedInUsername
        ) {
          setLoading(false);
          return;
        }

        if (showRefresh) {
          setRefreshing(true);
        }

        setError("");

        try {
          const data =
            await fetchBets();

          setBets(data);

          saveCache(data);
        } catch (loadError) {
          console.error(
            "Report error:",
            loadError
          );

          setError(
            "Failed to load report."
          );
        } finally {
          setLoading(false);
          setRefreshing(false);
        }
      },
      [
        loggedInUsername,
        fetchBets,
        saveCache,
      ]
    );

  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {
    if (
      !loggedInUsername
    ) {
      setLoading(false);
      return;
    }

    const cached =
      loadCache();

    if (
      cached &&
      Array.isArray(cached)
    ) {
      /*
       * IMPORTANT:
       * JSON cache converts Date -> string.
       *
       * Convert createdAt back to Date here.
       */
      const restored =
        cached
          .map(
            (bet) => {
              const createdAt =
                ensureDate(
                  bet.createdAt
                );

              if (!createdAt) {
                return null;
              }

              return {
                ...bet,
                createdAt,
              };
            }
          )
          .filter(Boolean);

      setBets(restored);

      setLoading(false);
    }

    loadReport(false);
  }, [
    loggedInUsername,
    loadCache,
    loadReport,
  ]);

  /* =======================================================
     AUTO REFRESH
  ======================================================= */

  useEffect(() => {
    if (
      !loggedInUsername
    ) {
      return undefined;
    }

    const interval =
      setInterval(
        () => {
          loadReport(false);
        },
        CACHE_TIME
      );

    return () => {
      clearInterval(
        interval
      );
    };
  }, [
    loggedInUsername,
    loadReport,
  ]);

  /* =======================================================
     DATE RANGE
  ======================================================= */

  const rangeStart =
    useMemo(() => {
      const date =
        new Date();

      date.setHours(
        0,
        0,
        0,
        0
      );

      date.setDate(
        date.getDate() -
        (period - 1)
      );

      return date;
    }, [
      period,
    ]);

  /* =======================================================
     FILTER BETS
  ======================================================= */

  const filteredBets =
    useMemo(() => {
      return bets.filter(
        (bet) => {
          const date =
            ensureDate(
              bet.createdAt
            );

          if (!date) {
            return false;
          }

          return (
            date.getTime() >=
            rangeStart.getTime()
          );
        }
      );
    }, [
      bets,
      rangeStart,
    ]);

  /* =======================================================
     GROUP DAILY
  ======================================================= */

  const dailyData =
    useMemo(() => {
      const map =
        new Map();

      for (
        let i = 0;
        i < period;
        i += 1
      ) {
        const date =
          new Date(
            rangeStart
          );

        date.setDate(
          rangeStart.getDate() +
          i
        );

        const key =
          getDateKey(date);

        if (!key) {
          continue;
        }

        map.set(
          key,
          {
            date,
            bets: [],
          }
        );
      }

      filteredBets.forEach(
        (bet) => {
          const date =
            ensureDate(
              bet.createdAt
            );

          if (!date) {
            return;
          }

          const key =
            getDateKey(date);

          if (
            key &&
            map.has(key)
          ) {
            map
              .get(key)
              .bets
              .push(bet);
          }
        }
      );

      return Array.from(
        map.values()
      ).map(
        (item) => {
          const values =
            item.bets;

          const stakes =
            values
              .map(
                (bet) =>
                  getBetStake(
                    bet
                  )
              )
              .filter(
                Number.isFinite
              );

          const open =
            stakes.length
              ? stakes[0]
              : 0;

          const close =
            stakes.length
              ? stakes[
                  stakes.length - 1
                ]
              : 0;

          const high =
            stakes.length
              ? Math.max(
                  ...stakes
                )
              : 0;

          const low =
            stakes.length
              ? Math.min(
                  ...stakes
                )
              : 0;

          const totalStake =
            values.reduce(
              (
                total,
                bet
              ) =>
                total +
                getBetStake(
                  bet
                ),
              0
            );

          const totalWin =
            values.reduce(
              (
                total,
                bet
              ) =>
                total +
                getBetWin(
                  bet
                ),
              0
            );

          const profit =
            values.reduce(
              (
                total,
                bet
              ) =>
                total +
                getBetProfit(
                  bet
                ),
              0
            );

          const wins =
            values.filter(
              isWin
            ).length;

          const losses =
            values.filter(
              isLoss
            ).length;

          return {
            date:
              item.date,

            open,

            high,

            low,

            close,

            totalStake,

            totalWin,

            profit,

            bets:
              values.length,

            wins,

            losses,
          };
        }
      );
    }, [
      filteredBets,
      period,
      rangeStart,
    ]);

  /* =======================================================
     WEEK DATA
  ======================================================= */

  const weeklyData =
    useMemo(() => {
      const weeks = [];

      const end =
        new Date();

      end.setHours(
        0,
        0,
        0,
        0
      );

      const start =
        new Date(end);

      start.setDate(
        start.getDate() -
        27
      );

      let cursor =
        new Date(start);

      while (
        cursor <= end
      ) {
        const weekStart =
          new Date(cursor);

        const weekEnd =
          new Date(cursor);

        weekEnd.setDate(
          weekEnd.getDate() +
          6
        );

        if (
          weekEnd > end
        ) {
          weekEnd.setTime(
            end.getTime()
          );
        }

        const weekEndInclusive =
          new Date(
            weekEnd.getTime() +
            86400000 -
            1
          );

        const weekBets =
          bets.filter(
            (bet) => {
              const date =
                ensureDate(
                  bet.createdAt
                );

              if (!date) {
                return false;
              }

              return (
                date.getTime() >=
                  weekStart.getTime() &&
                date.getTime() <=
                  weekEndInclusive.getTime()
              );
            }
          );

        const values =
          weekBets.map(
            (bet) =>
              getBetStake(
                bet
              )
          );

        weeks.push({
          start:
            weekStart,

          end:
            weekEnd,

          date:
            weekStart,

          open:
            values[0] || 0,

          high:
            values.length
              ? Math.max(
                  ...values
                )
              : 0,

          low:
            values.length
              ? Math.min(
                  ...values
                )
              : 0,

          close:
            values[
              values.length - 1
            ] || 0,

          bets:
            weekBets.length,

          profit:
            weekBets.reduce(
              (
                total,
                bet
              ) =>
                total +
                getBetProfit(
                  bet
                ),
              0
            ),
        });

        cursor.setDate(
          cursor.getDate() +
          7
        );
      }

      return weeks;
    }, [
      bets,
    ]);

  /* =======================================================
     MONTHLY DATA
  ======================================================= */

  const monthlyData =
    useMemo(() => {
      const map =
        new Map();

      bets.forEach(
        (bet) => {
          /*
           * FIX FOR:
           *
           * date.getFullYear is not a function
           *
           * Never use bet.createdAt directly.
           */
          const date =
            ensureDate(
              bet.createdAt
            );

          if (!date) {
            return;
          }

          const key =
            `${date.getFullYear()}-${String(
              date.getMonth() + 1
            ).padStart(
              2,
              "0"
            )}`;

          if (
            !map.has(key)
          ) {
            map.set(
              key,
              {
                date,
                bets: [],
              }
            );
          }

          map
            .get(key)
            .bets
            .push(bet);
        }
      );

      return Array.from(
        map.entries()
      )
        .sort(
          (
            a,
            b
          ) => {
            const dateA =
              ensureDate(
                a[1].date
              );

            const dateB =
              ensureDate(
                b[1].date
              );

            return (
              (dateA?.getTime() || 0) -
              (dateB?.getTime() || 0)
            );
          }
        )
        .slice(-12)
        .map(
          (
            [
              key,
              item,
            ]
          ) => {
            const values =
              item.bets.map(
                (bet) =>
                  getBetStake(
                    bet
                  )
              );

            return {
              key,

              date:
                ensureDate(
                  item.date
                ),

              open:
                values[0] || 0,

              high:
                values.length
                  ? Math.max(
                      ...values
                    )
                  : 0,

              low:
                values.length
                  ? Math.min(
                      ...values
                    )
                  : 0,

              close:
                values[
                  values.length - 1
                ] || 0,

              bets:
                item.bets.length,

              profit:
                item.bets.reduce(
                  (
                    total,
                    bet
                  ) =>
                    total +
                    getBetProfit(
                      bet
                    ),
                  0
                ),
            };
          }
        );
    }, [
      bets,
    ]);

  /* =======================================================
     CHART DATA
  ======================================================= */

  const chartData =
    useMemo(() => {
      if (
        chartMode ===
        "weekly"
      ) {
        return weeklyData;
      }

      if (
        chartMode ===
        "monthly"
      ) {
        return monthlyData;
      }

      return dailyData;
    }, [
      chartMode,
      dailyData,
      weeklyData,
      monthlyData,
    ]);

  /* =======================================================
     APEX SERIES
  ======================================================= */

  const chartSeries =
    useMemo(() => {
      return [
        {
          name:
            "Bet Activity",

          data:
            chartData
              .map(
                (item) => {
                  const date =
                    ensureDate(
                      item.date
                    );

                  if (!date) {
                    return null;
                  }

                  return {
                    x:
                      date.getTime(),

                    y: [
                      safeNumber(
                        item.open
                      ),

                      safeNumber(
                        item.high
                      ),

                      safeNumber(
                        item.low
                      ),

                      safeNumber(
                        item.close
                      ),
                    ],
                  };
                }
              )
              .filter(Boolean),
        },
      ];
    }, [
      chartData,
    ]);

  /* =======================================================
     CHART OPTIONS
  ======================================================= */

  const chartOptions =
    useMemo(
      () => ({
        chart: {
          type:
            "candlestick",

          height:
            420,

          toolbar: {
            show: true,
          },

          zoom: {
            enabled: true,
          },
        },

        title: {
          text:
            chartMode ===
            "daily"
              ? `${PERIODS[period]} Report`
              : chartMode ===
                "weekly"
              ? "Weekly Report"
              : "Monthly Report",

          align:
            "left",
        },

        xaxis: {
          type:
            "datetime",

          labels: {
            datetimeUTC:
              false,
          },
        },

        yaxis: {
          tooltip: {
            enabled: true,
          },

          labels: {
            formatter:
              (value) =>
                formatNumber(
                  value
                ),
          },
        },

        tooltip: {
          custom:
            ({
              dataPointIndex,
            }) => {
              const item =
                chartData[
                  dataPointIndex
                ];

              if (!item) {
                return "";
              }

              return `
                <div class="report-tooltip">
                  <strong>
                    ${formatDate(
                      item.date
                    )}
                  </strong>

                  <div>
                    Open:
                    ${formatNumber(
                      item.open
                    )}
                  </div>

                  <div>
                    High:
                    ${formatNumber(
                      item.high
                    )}
                  </div>

                  <div>
                    Low:
                    ${formatNumber(
                      item.low
                    )}
                  </div>

                  <div>
                    Close:
                    ${formatNumber(
                      item.close
                    )}
                  </div>

                  <div>
                    Bets:
                    ${formatNumber(
                      item.bets
                    )}
                  </div>

                  <div>
                    Profit:
                    ${formatNumber(
                      item.profit
                    )}
                  </div>
                </div>
              `;
            },
        },

        plotOptions: {
          candlestick: {
            colors: {
              upward:
                "#16a34a",

              downward:
                "#ef4444",
            },

            wick: {
              useFillColor:
                true,
            },
          },
        },

        noData: {
          text:
            "No betting data",
        },
      }),
      [
        chartData,
        chartMode,
        period,
      ]
    );

  /* =======================================================
     SUMMARY
  ======================================================= */

  const summary =
    useMemo(() => {
      const totalStake =
        filteredBets.reduce(
          (
            total,
            bet
          ) =>
            total +
            getBetStake(
              bet
            ),
          0
        );

      const totalWin =
        filteredBets.reduce(
          (
            total,
            bet
          ) =>
            total +
            getBetWin(
              bet
            ),
          0
        );

      const profit =
        filteredBets.reduce(
          (
            total,
            bet
          ) =>
            total +
            getBetProfit(
              bet
            ),
          0
        );

      const wins =
        filteredBets.filter(
          isWin
        ).length;

      const losses =
        filteredBets.filter(
          isLoss
        ).length;

      return {
        totalStake,

        totalWin,

        profit,

        wins,

        losses,

        bets:
          filteredBets.length,
      };
    }, [
      filteredBets,
    ]);

  /* =======================================================
     LOGIN
  ======================================================= */

  if (
    !loggedInUsername
  ) {
    return (
      <div className="report-wrapper">
        <div className="report-empty">
          <FaUsers />

          <h3>
            Login Required
          </h3>

          <p>
            Please login first to view
            your betting report.
          </p>

          <button
            type="button"
            onClick={() =>
              navigate(-1)
            }
          >
            <FaArrowLeft />

            Back
          </button>
        </div>
      </div>
    );
  }

  /* =======================================================
     LOADING
  ======================================================= */

  if (
    loading
  ) {
    return (
      <div className="report-wrapper">
        <div className="report-loading">
          <div className="report-spinner" />

          <strong>
            Loading report...
          </strong>

          <span>
            Preparing your betting chart
          </span>
        </div>
      </div>
    );
  }

  /* =======================================================
     MAIN
  ======================================================= */

  return (
    <div className="report-wrapper">

      {/* HEADER */}

      <div className="report-header">

        <button
          type="button"
          className="report-back"
          onClick={() =>
            navigate(-1)
          }
        >
          <FaArrowLeft />

          <span>
            Back
          </span>
        </button>

        <div className="report-title">

          <div className="report-title-icon">
            <FaChartLine />
          </div>

          <div>
            <span>
              BETTING ANALYTICS
            </span>

            <h1>
              Report
            </h1>
          </div>

        </div>

        <button
          type="button"
          className={
            refreshing
              ? "report-refresh spinning"
              : "report-refresh"
          }
          onClick={() =>
            loadReport(true)
          }
          title="Refresh"
        >
          <FaSyncAlt />
        </button>

      </div>

      {/* ERROR */}

      {error && (
        <div className="report-error">

          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={() =>
              loadReport(true)
            }
          >
            Retry
          </button>

        </div>
      )}

      {/* PERIOD */}

      <div className="report-period">

        <div className="report-period-title">

          <FaCalendarAlt />

          <span>
            REPORT PERIOD
          </span>

        </div>

        <div className="report-period-buttons">

          {Object.entries(
            PERIODS
          ).map(
            (
              [
                value,
                label,
              ]
            ) => (
              <button
                key={value}
                type="button"
                className={
                  period ===
                  Number(value)
                    ? "active"
                    : ""
                }
                onClick={() => {
                  setPeriod(
                    Number(value)
                  );

                  setChartMode(
                    "daily"
                  );
                }}
              >
                {label}
              </button>
            )
          )}

          <button
            type="button"
            className={
              chartMode ===
              "weekly"
                ? "active"
                : ""
            }
            onClick={() =>
              setChartMode(
                "weekly"
              )
            }
          >
            Week
          </button>

          <button
            type="button"
            className={
              chartMode ===
              "monthly"
                ? "active"
                : ""
            }
            onClick={() =>
              setChartMode(
                "monthly"
              )
            }
          >
            Month
          </button>

        </div>

      </div>

      {/* SUMMARY */}

      <div className="report-summary">

        <div className="report-stat">

          <div className="report-stat-icon">
            <FaMoneyBillWave />
          </div>

          <div>
            <span>
              TOTAL STAKE
            </span>

            <strong>
              {formatNumber(
                summary.totalStake
              )}
            </strong>

            <small>
              TZS
            </small>
          </div>

        </div>

        <div className="report-stat">

          <div className="report-stat-icon">
            <FaFutbol />
          </div>

          <div>
            <span>
              TOTAL BETS
            </span>

            <strong>
              {summary.bets}
            </strong>
          </div>

        </div>

        <div className="report-stat">

          <div className="report-stat-icon">
            <FaTrophy />
          </div>

          <div>
            <span>
              WINS / LOSSES
            </span>

            <strong>
              {summary.wins}
              {" / "}
              {summary.losses}
            </strong>
          </div>

        </div>

        <div className="report-stat">

          <div className="report-stat-icon">
            <FaChartLine />
          </div>

          <div>
            <span>
              PROFIT
            </span>

            <strong
              className={
                summary.profit < 0
                  ? "negative"
                  : "positive"
              }
            >
              {formatNumber(
                summary.profit
              )}
            </strong>

            <small>
              TZS
            </small>
          </div>

        </div>

      </div>

      {/* CHART */}

      <div className="report-chart-card">

        <div className="report-chart-header">

          <div>

            <span>
              CANDLESTICK ANALYSIS
            </span>

            <h2>
              {chartMode ===
              "daily"
                ? PERIODS[
                    period
                  ]
                : chartMode ===
                  "weekly"
                ? "Weekly"
                : "Monthly"}
            </h2>

          </div>

          <div className="report-chart-info">

            <span>
              O Open
            </span>

            <span>
              H High
            </span>

            <span>
              L Low
            </span>

            <span>
              C Close
            </span>

          </div>

        </div>

        <div className="report-chart">

          {chartData.length > 0 ? (
            <Chart
              options={
                chartOptions
              }
              series={
                chartSeries
              }
              type="candlestick"
              height={420}
            />
          ) : (
            <div className="report-no-data">

              <FaChartLine />

              <strong>
                No betting data
              </strong>

              <span>
                There is no data available
                for this period.
              </span>

            </div>
          )}

        </div>

      </div>

      {/* TABLE */}

      <div className="report-table-card">

        <div className="report-table-header">

          <div>

            <span>
              ACTIVITY BREAKDOWN
            </span>

            <h2>
              {chartMode ===
              "daily"
                ? "Daily Report"
                : chartMode ===
                  "weekly"
                ? "Weekly Report"
                : "Monthly Report"}
            </h2>

          </div>

        </div>

        <div className="report-table-wrapper">

          <table>

            <thead>

              <tr>

                <th>
                  DATE
                </th>

                <th>
                  OPEN
                </th>

                <th>
                  HIGH
                </th>

                <th>
                  LOW
                </th>

                <th>
                  CLOSE
                </th>

                <th>
                  BETS
                </th>

                <th>
                  PROFIT
                </th>

              </tr>

            </thead>

            <tbody>

              {chartData
                .slice()
                .reverse()
                .map(
                  (
                    item,
                    index
                  ) => (

                    <tr
                      key={
                        `${
                          getDateKey(
                            item.date
                          ) ||
                          "date"
                        }-${index}`
                      }
                    >

                      <td>
                        {formatShortDate(
                          item.date
                        )}
                      </td>

                      <td>
                        {formatNumber(
                          item.open
                        )}
                      </td>

                      <td>
                        {formatNumber(
                          item.high
                        )}
                      </td>

                      <td>
                        {formatNumber(
                          item.low
                        )}
                      </td>

                      <td>
                        {formatNumber(
                          item.close
                        )}
                      </td>

                      <td>
                        {item.bets}
                      </td>

                      <td
                        className={
                          item.profit < 0
                            ? "negative"
                            : "positive"
                        }
                      >
                        {formatNumber(
                          item.profit
                        )}
                      </td>

                    </tr>

                  )
                )}

            </tbody>

          </table>

        </div>

      </div>

      {/* FOOTER */}

      <div className="report-footer">

        <span>
          AGENCY BFC REPORT
        </span>

        <small>
          Candlestick analysis based on
          betting activity.
        </small>

      </div>

    </div>
  );
};

export default Report;
